import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import gql from 'graphql-tag';
import {Apollo} from 'apollo-angular';
import {UntypedFormControl} from '@angular/forms';
import {combineLatest, EMPTY, fromEvent, merge, of, Subscription} from 'rxjs';
import {ImageTypeEnum} from '../../shared/graphql/globalTypes';
import {catchError, debounceTime, map, switchMap, withLatestFrom} from 'rxjs/operators';
import {
  GetContent,
  GetContent_content_edges_node,
  GetContentVariables,
} from './__generated__/GetContent';
import {countPages} from '../../shared/utils/table.utils';
import {ErrorService} from '../../shared/services/error.service';
import {
  GetContentTemplates,
  GetContentTemplates_templates_edges,
} from './__generated__/GetContentTemplates';
import {
  GetTotalContentCount,
  GetTotalContentCountVariables,
} from './__generated__/GetTotalContentCount';
import {BsModalRef, BsModalService} from 'ngx-bootstrap/modal';
import {ContentDeleteComponent} from '../../shared/modals/content-delete/content-delete.component';
import {
  getSpecificTimeSelection,
  mapTimeSelection,
  SpecificTimeSelection,
} from '../../shared/utils/date-sort.utils';

interface TemplateQueryItems {
  id: string;
  title: string;
  type: string;
}

const contentGetQuery = gql`
  query GetContent($filterType: ImageTypeEnum, $offset: Int, $first: Int, $templateId: String, $templateItemId: String, $orderBy: String, $createdGte: DateTime, $createdLt: DateTime) {
    content(filterType: $filterType, offset: $offset, first: $first, templateId: $templateId, templateItemId: $templateItemId, orderBy: [$orderBy], created_Gte: $createdGte, created_Lt: $createdLt) {
      totalCount
      edges {
        node {
          id
          templateItem {
            id
            title
            template {
              id
              title
              items {
                id
              }
            }
          }
          type
          title
          views {
            totalCount
          }
          deleted
          deletedReason
        }
      }
    }
  }
`;

const contentTemplatesGetQuery = gql`
  query GetContentTemplates {
    templates {
      edges {
        node {
          id
          title
          items {
            id
            title
          }
        }
      }
    }
  }
`;

const totalContentCountGetQuery = gql`
  query GetTotalContentCount($filterType: ImageTypeEnum, $templateId: String, $templateItemId: String) {
    content(filterType: $filterType, templateId: $templateId templateItemId: $templateItemId) {
      totalCount
    }
  }
`;

@Component({
  selector: 'app-video',
  templateUrl: './content.component.html',
  styleUrls: ['./content.component.scss'],
  standalone: false,
})
export class ContentComponent implements OnInit, OnDestroy {
  content: GetContent_content_edges_node[] = [];
  templates: GetContentTemplates_templates_edges[] = [];
  templateQueryItems: TemplateQueryItems[] = [];
  dropdown = ['Alle campagnes'];
  selection = new UntypedFormControl(null);
  ImageTypeEnum = ImageTypeEnum;
  first = new UntypedFormControl(24);
  offset = new UntypedFormControl(1);
  templateSelect = new UntypedFormControl(null);
  monthSelection = new UntypedFormControl(null);
  specificTimeSelections: SpecificTimeSelection[] = [];
  pageCount = 1;
  pages: number[] = [];
  totalCount: number | null = null;
  loading = true;
  bsModalRef: BsModalRef | null = null;
  activeDropdown: {[key: string]: boolean} = {};
  templateId = '';
  templateItemId = '';
  initialRoute = true;
  private subscriptions: Subscription[] = [];

  constructor(
    private router: Router,
    private apollo: Apollo,
    private errService: ErrorService,
    private route: ActivatedRoute,
    private modalService: BsModalService,
  ) {}

  ngOnInit(): void {
    this.specificTimeSelections = getSpecificTimeSelection();
    this.monthSelection.setValue(this.specificTimeSelections[6]);

    const currentPaging$ = merge(this.first.valueChanges, of(this.first.value));
    const offset$ = merge(this.offset.valueChanges, of(this.offset.value));
    const templateFilter$ = merge(this.templateSelect.valueChanges, of(this.templateSelect.value));

    const selection$ = merge(this.selection.valueChanges, of(this.selection.value));
    const monthSelection$ = merge(this.monthSelection.valueChanges, of(this.monthSelection.value));
    const created$ = monthSelection$.pipe(
      map(filter => {
        if (filter === null) {
          return null;
        }
        return mapTimeSelection(filter);
      }),
    );

    const content$ = combineLatest([
      currentPaging$,
      selection$,
      offset$,
      templateFilter$,
      created$,
    ]).pipe(
      debounceTime(50),
      switchMap(filter => {
        return this.apollo
          .watchQuery<GetContent, GetContentVariables>({
            query: contentGetQuery,
            variables: {
              filterType: filter[1],
              first: filter[0],
              offset: filter[0] * (filter[2] - 1),
              templateId: this.templateId,
              templateItemId: this.templateItemId,
              createdGte: filter[4] ? filter[4].gte : null,
              createdLt: filter[4] ? filter[4].lt : null,
              orderBy: '-created',
            },
          })
          .valueChanges.pipe(
            catchError(error => {
              this.errService.handleDefault(error);
              return EMPTY;
            }),
          );
      }),
    );

    const totalCount$ = combineLatest([selection$, templateFilter$]).pipe(
      debounceTime(50),
      switchMap(filter => {
        return this.apollo
          .watchQuery<GetTotalContentCount, GetTotalContentCountVariables>({
            query: totalContentCountGetQuery,
            variables: {
              filterType: filter[0],
              templateId: this.templateId,
              templateItemId: this.templateItemId,
            },
          })
          .valueChanges.pipe(
            catchError(error => {
              this.errService.handleDefault(error);
              return EMPTY;
            }),
          );
      }),
    );

    this.subscriptions.push(
      selection$.subscribe(() => {
        this.offset.setValue(1);
      }),
      content$.subscribe(output => {
        const content = output.data?.content;
        this.content = (content?.edges ?? []).map(e => e.node);
        this.loading = false;

        this.pageCount = countPages(content?.totalCount ?? 0, this.first.value);
        this.pages = Array(Math.min(this.pageCount, 1000))
          .fill(0)
          .map((x, i) => i);
      }),
      totalCount$.subscribe(output => {
        this.totalCount = output.data?.content.totalCount ?? null;
      }),
      this.apollo
        .watchQuery<GetContentTemplates>({
          query: contentTemplatesGetQuery,
        })
        .valueChanges.pipe(
          catchError(error => {
            this.errService.handleDefault(error);
            return EMPTY;
          }),
          withLatestFrom(this.route.queryParams),
        )
        .subscribe(([output, params]) => {
          this.templates = output.data?.templates.edges ?? [];
          this.templateQueryItems = [];
          this.templates.forEach(template => {
            this.templateQueryItems.push({
              id: template.node.id,
              title: template.node.title,
              type: 'template',
            });
            if (template.node.items.length > 1) {
              template.node.items.forEach(templateItem => {
                this.templateQueryItems.push({
                  id: templateItem.id,
                  title: template.node.title + ' / ' + templateItem.title,
                  type: 'templateItem',
                });
              });
            }
          });

          this.templateQueryItems.sort((a, b) => a.title.localeCompare(b.title));

          if (params.templateId) {
            this.templateSelect.setValue(
              this.templateQueryItems.find(
                item => item.type === 'template' && item.id === params.templateId,
              ),
            );
          } else if (params.templateItemId) {
            this.templateSelect.setValue(
              this.templateQueryItems.find(
                item => item.type === 'templateItem' && item.id === params.templateItemId,
              ),
            );
          }
        }),
      templateFilter$.subscribe(output => {
        this.offset.setValue(1);
        this.templateId = '';
        this.templateItemId = '';
        // need to check if its the initial route or not because output will always be null the first time. Because templates/templateItems are not set correctly yet
        if (output === null || output === undefined) {
          if (this.initialRoute) {
            this.initialRoute = false;
            return;
          }
          this.router.navigate([]);
          return;
        }
        if (output.type === 'template') {
          this.templateId = output.id;
          this.router.navigate([], {queryParams: {templateId: output.id}});
        }
        if (output.type === 'templateItem') {
          this.templateItemId = output.id;
          this.router.navigate([], {queryParams: {templateItemId: output.id}});
        }
      }),
      fromEvent(window, 'scroll').subscribe(() => (this.activeDropdown = {})),
    );
  }

  toContentView(id: string): void {
    this.router.navigate(['content', id]);
  }

  nextPage(): void {
    this.offset.setValue(this.offset.value + 1);
  }

  previousPage(): void {
    this.offset.setValue(this.offset.value - 1);
  }

  deleteContent(id: string): void {
    this.bsModalRef = this.modalService.show(ContentDeleteComponent);
    this.bsModalRef.content.contentId = id;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => {
      subscription.unsubscribe();
    });
  }
}
